import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SortIcon from '@mui/icons-material/Sort';
// Item category icons
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ScienceIcon from '@mui/icons-material/Science';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TerrainIcon from '@mui/icons-material/Terrain';
// Additional specific icons for different pack types and items
import StyleIcon from '@mui/icons-material/Style';
import StarIcon from '@mui/icons-material/Star';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import PaletteIcon from '@mui/icons-material/Palette';

const ItemFilters = ({ onFiltersChange, items = [], isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState('ALL');
  const [sortBy, setSortBy] = useState('price_asc'); // Default: cheapest first

  // Available asset types for filtering (based on your requirements)
  const assetTypes = [
    { value: 'ALL', label: 'All Items', icon: <ViewInArIcon />, color: colors.grey[400] },
    { value: 'PACKS', label: 'Card Packs', icon: <StyleIcon />, color: colors.blueAccent[400] },
    { value: 'TOTEMS', label: 'Totems', icon: <AutoFixHighIcon />, color: colors.greenAccent[400] },
    { value: 'SKINS', label: 'Skins', icon: <PaletteIcon />, color: colors.redAccent[400] },
    { value: 'CONSUMABLES', label: 'Consumables', icon: <ScienceIcon />, color: colors.purple[400] },
    { value: 'TITLES', label: 'Titles', icon: <StarIcon />, color: colors.yellow[400] },
    { value: 'LAND', label: 'Land Claims', icon: <TerrainIcon />, color: colors.brown[400] }
  ];

  // Sort options
  const sortOptions = [
    { value: 'price_asc', label: 'Price (Low to High)' },
    { value: 'price_desc', label: 'Price (High to Low)' },
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'listed_desc', label: 'Most Listed' },
    { value: 'listed_asc', label: 'Least Listed' }
  ];

  // Send filter changes to parent component
  useEffect(() => {
    const filters = {
      searchTerm,
      selectedAssetType,
      sortBy
    };
    onFiltersChange && onFiltersChange(filters);
  }, [searchTerm, selectedAssetType, sortBy, onFiltersChange]);

  // Toggle asset type filter
  const toggleAssetType = (assetTypeValue) => {
    setSelectedAssetType(assetTypeValue);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedAssetType('ALL');
    setSortBy('price_asc');
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || selectedAssetType !== 'ALL' || sortBy !== 'price_asc';

  // Get current asset type info
  const currentAssetType = assetTypes.find(type => type.value === selectedAssetType);  return (
    <Box sx={{ 
      px: isMobile ? 1 : 2, 
      py: isMobile ? 1 : 2,
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Header with Clear Button */}
      <Box display="flex" alignItems="center" gap={1} mb={isMobile ? 1 : 2}>
        <FilterListIcon sx={{ color: colors.greenAccent[400], fontSize: isMobile ? '1rem' : '1.2rem' }} />
        <Typography
          variant="h6"
          color={colors.grey[100]}
          sx={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: 'bold' }}
        >
          Item Filters
        </Typography>
        {hasActiveFilters && (
          <Chip 
            label={`${[searchTerm, selectedAssetType !== 'ALL', sortBy !== 'price_asc'].filter(Boolean).length}`}
            size="small"
            sx={{ 
              backgroundColor: colors.redAccent[600],
              color: colors.grey[100],
              fontSize: isMobile ? '0.6rem' : '0.7rem',
              height: isMobile ? '16px' : '20px'
            }}
          />
        )}
      </Box>

      {hasActiveFilters && (
        <Button 
          size="small" 
          onClick={clearFilters}
          variant="outlined"
          sx={{
            color: colors.grey[300],
            borderColor: colors.primary[300],
            fontSize: isMobile ? '0.65rem' : '0.7rem',
            mb: isMobile ? 1 : 2,
            width: '100%',
            py: isMobile ? 0.25 : 0.5
          }}
        >
          Clear All Filters
        </Button>
      )}

      <Box display="flex" flexDirection="column" gap={isMobile ? 1 : 2}>
        {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search items..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.grey[400] }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setSearchTerm('')}
                sx={{ color: colors.grey[400] }}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            color: colors.grey[100],
            fontSize: '0.8rem',
            '& fieldset': { borderColor: colors.primary[300] },
            '&:hover fieldset': { borderColor: colors.greenAccent[400] },
            '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] }
          },
          '& .MuiInputLabel-root': { 
            color: colors.grey[300],
            fontSize: '0.8rem'
          }
        }}
      />

      {/* Asset Type Filter - Text Chips */}
      <Box>
        <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
          Asset Category
        </Typography>
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: 1,
            p: 1,
            mb: 2
          }}
        >
          {assetTypes.map(type => {
            const isSelected = selectedAssetType === type.value;

            return (
              <Chip
                key={type.value}
                label={type.label}
                size="small"
                onClick={() => toggleAssetType(type.value)}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: isSelected ? type.color : 'transparent',
                  color: isSelected ? '#ffffff' : type.color,
                  border: `2px solid ${isSelected ? type.color : colors.grey[600]}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: isSelected ? type.color : `${type.color}20`,
                    borderColor: type.color
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Sort By */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: colors.grey[300], fontSize: '0.8rem' }}>Sort By</InputLabel>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          label="Sort By"
          sx={{
            color: colors.grey[100],
            fontSize: '0.8rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[300]
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.greenAccent[400]
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.greenAccent[400]
            }
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8rem' }}>
              <Box display="flex" alignItems="center">
                <SortIcon sx={{ mr: 1, fontSize: '1rem' }} />
                <Typography sx={{ fontSize: '0.8rem' }}>{option.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box>
          <Typography variant="subtitle2" color={colors.grey[300]} sx={{ mb: 1 }}>
            Active Filters:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedAssetType !== 'ALL' && (
              <Chip
                label={currentAssetType?.label}
                size="small"
                onDelete={() => setSelectedAssetType('ALL')}
                sx={{
                  backgroundColor: colors.blueAccent[800],
                  color: colors.grey[100],
                  '& .MuiChip-deleteIcon': {
                    color: colors.grey[300]
                  }
                }}
              />
            )}
            {searchTerm && (
              <Chip
                label={`Search: ${searchTerm}`}
                size="small"
                onDelete={() => setSearchTerm('')}
                sx={{
                  backgroundColor: colors.blueAccent[800],
                  color: colors.grey[100],
                  '& .MuiChip-deleteIcon': {
                    color: colors.grey[300]
                  }
                }}
              />
            )}
            {sortBy !== 'price_asc' && (
              <Chip
                label={`Sort: ${sortOptions.find(s => s.value === sortBy)?.label}`}
                size="small"
                onDelete={() => setSortBy('price_asc')}
                sx={{
                  backgroundColor: colors.blueAccent[800],
                  color: colors.grey[100],
                  '& .MuiChip-deleteIcon': {
                    color: colors.grey[300]
                  }
                }}
              />
            )}
          </Box>
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default ItemFilters;
